import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Hand, MapPin, UserMinus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { STATUSES, PRIORITIES, QRC_TYPES, fmtDateLong } from "@/lib/constants";
import { Pill } from "@/components/Pill";
import { NotesThread } from "@/components/dashboard/NotesThread";
import { TicketConversation } from "@/components/dashboard/TicketConversation";
import { TemplatePicker } from "@/components/Conversation";
import { NameWithAvatar } from "@/components/Avatar";
import { ActivityLog, AdminControls } from "@/components/dashboard/TicketExtras";
import { useAuth } from "@/context/AuthContext";

function Info({ label, children, testId }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <div className="mt-0.5 text-base font-medium break-words" data-testid={testId}>{children || "—"}</div>
    </div>
  );
}

function EditSelect({ label, value, onChange, options, kind, testId }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 rounded-xl border-2 border-black bg-white font-semibold" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-2 border-black">
          {options.map((o) => (
            <SelectItem key={o} value={o} className="py-2.5"><Pill kind={kind} value={o} /></SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function TicketDrawer({ ticket, onClose }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [notes, setNotes] = useState([]);
  const [live, setLive] = useState(null);

  useEffect(() => {
    if (ticket) {
      setDraft({ status: ticket.status, priority: ticket.priority, qrc_type: ticket.qrc_type, resolution_notes: ticket.resolution_notes || "" });
      setNotes(ticket.notes || []);
      setLive(ticket);
    }
  }, [ticket]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["tickets"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["my-performance"] });
    qc.invalidateQueries({ queryKey: ["leaderboard"] });
    qc.invalidateQueries({ queryKey: ["escalations"] });
  };

  const save = useMutation({
    mutationFn: () => api.patch(`/tickets/${ticket.id}`, draft).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`${data.ticket_id} updated${data.status === "Resolved" ? " · closing summary emailed" : ""}`);
      refresh();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const assign = useMutation({
    mutationFn: (action) => api.post(`/tickets/${ticket.id}/${action}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Assignment updated");
      refresh();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!ticket || !draft || !live) return <Sheet open={false} />;
  const canAssign = !ticket.assigned_to_id;
  const resolving = draft.status === "Resolved" && ticket.status !== "Resolved";
  const summaryMissing = resolving && draft.resolution_notes.trim().length < 10;
  const templateVars = { name: ticket.name.split(" ")[0], full_name: ticket.name, ticket_id: ticket.ticket_id, product: ticket.product, query: ticket.query, email: ticket.email, phone: ticket.phone, batch: ticket.batch || "", agent: user.name };
  const dirty = JSON.stringify(draft) !== JSON.stringify({ status: ticket.status, priority: ticket.priority, qrc_type: ticket.qrc_type, resolution_notes: ticket.resolution_notes || "" });

  return (
    <Sheet open={Boolean(ticket)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-l-2 border-black p-0 sm:max-w-xl" data-testid="ticket-drawer">
        <SheetHeader className="border-b-2 border-black bg-[#FACC15] px-6 py-5 text-left">
          <SheetTitle className="flex flex-wrap items-center gap-3 font-display text-2xl font-extrabold">
            <span className="font-mono" data-testid="drawer-ticket-id">{ticket.ticket_id}</span>
            <Pill kind="status" value={ticket.status} />
            <Pill kind="level" value={ticket.escalation_level} />
            {live.resubmit_count > 0 && <span className="pill border-black bg-white" data-testid="drawer-resubmit-badge">re-submitted {live.resubmit_count}×</span>}
          </SheetTitle>
          <SheetDescription className="text-black/80">Created {fmtDateLong(ticket.created_at)}</SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-6 py-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Info label="Name" testId="drawer-name">{ticket.name}</Info>
            <Info label="Phone (WhatsApp)">{ticket.phone}</Info>
            <Info label="Email">{ticket.email}</Info>
            <Info label="Product">{ticket.product}</Info>
            <div className="sm:col-span-2"><Info label="Query" testId="drawer-query">{ticket.query}</Info></div>
            {ticket.addons && <div className="sm:col-span-2"><Info label="Missing add-ons">{ticket.addons}</Info></div>}
            {ticket.batch && <Info label="Batch">{ticket.batch}</Info>}
            <Info label="Indore offline classes">
              {ticket.offline_interest ? <span className="inline-flex items-center gap-1 font-bold text-[#B45309]"><MapPin className="h-4 w-4" /> Interested</span> : "No"}
            </Info>
            {ticket.comment && <div className="sm:col-span-2"><Info label="Comment"><p className="whitespace-pre-wrap rounded-lg bg-zinc-50 p-3 text-sm">{ticket.comment}</p></Info></div>}
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-xl border-2 border-black p-4 text-sm">
            <Info label="SLA due">{fmtDateLong(ticket.sla_due_at)}</Info>
            <Info label="Working hours elapsed">{ticket.elapsed_working_hours}h</Info>
            <Info label="Assigned to" testId="drawer-assigned-to">{ticket.assigned_to ? <NameWithAvatar id={ticket.assigned_to_id} name={ticket.assigned_to} size={24} /> : "Unassigned"}</Info>
            <Info label="Resolved at">{ticket.resolved_at ? fmtDateLong(ticket.resolved_at) : "—"}</Info>
            {user.role === "admin" && live.rating && <div className="col-span-2"><Info label="Customer rating (admin only)" testId="drawer-rating">{live.rating.score}/5{live.rating.comment ? ` — ${live.rating.comment}` : ""}</Info></div>}
          </div>

          <div className="flex flex-wrap gap-2">
            {canAssign && (
              <button className="btn-primary" onClick={() => assign.mutate("assign")} disabled={assign.isPending} data-testid="drawer-assign-to-me-button">
                <Hand className="h-4 w-4" /> Assign to me
              </button>
            )}
            {!canAssign && user.role === "admin" && (
              <button className="btn-ghost" onClick={() => assign.mutate("unassign")} disabled={assign.isPending} data-testid="drawer-unassign-button">
                <UserMinus className="h-4 w-4" /> Unassign
              </button>
            )}
          </div>

          {user.role === "admin" && <AdminControls ticket={live} onUpdated={setLive} onClose={onClose} />}

          <TicketConversation ticket={live} onUpdated={setLive} />

          <NotesThread ticketId={ticket.id} notes={notes} onUpdated={(t) => setNotes(t.notes || [])} />

          <ActivityLog activity={live.activity} />

          <div className="space-y-4 border-t-2 border-dashed border-zinc-300 pt-6">
            <h3 className="font-display text-lg font-bold">Update ticket</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <EditSelect label="Status" value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={STATUSES} kind="status" testId="ticket-drawer-status-select" />
              <EditSelect label="Priority" value={draft.priority} onChange={(v) => setDraft({ ...draft, priority: v })} options={PRIORITIES} kind="priority" testId="ticket-drawer-priority-select" />
              <EditSelect label="QRC type" value={draft.qrc_type} onChange={(v) => setDraft({ ...draft, qrc_type: v })} options={QRC_TYPES} kind="qrc" testId="ticket-drawer-qrc-select" />
            </div>
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="field-label">Closing summary {resolving && <span className="text-[#B91C1C]">· required to resolve</span>}</label>
                <TemplatePicker vars={templateVars} onPick={(v) => setDraft({ ...draft, resolution_notes: v })} kind="closing" label="Closing templates" testId="closing-template-picker" />
              </div>
              <textarea
                className={`field-input min-h-[120px] ${summaryMissing ? "!border-[#DC2626]" : ""}`}
                value={draft.resolution_notes}
                onChange={(e) => setDraft({ ...draft, resolution_notes: e.target.value })}
                placeholder="Describe how this ticket was resolved — this is emailed to the customer on closure"
                data-testid="ticket-drawer-notes-textarea"
              />
              {summaryMissing && <p className="mt-1 text-xs font-semibold text-[#B91C1C]" data-testid="closing-summary-required">Write a closing summary (at least 10 characters) before resolving.</p>}
            </div>
            {draft.status === "Resolved" && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[#DCFCE7] p-3 text-sm">
                <span className="font-semibold">On save, the customer receives the closing summary by email with a link to rate & review our support.</span>
              </div>
            )}
            <button className="btn-dark w-full" onClick={() => save.mutate()} disabled={!dirty || save.isPending || summaryMissing} data-testid="ticket-drawer-save-button">
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
